import React from "react";
import { useId } from "react-id-generator";
import { useDebounce } from "use-debounce";

import {
  isKintoneSubtableFormField,
  KintoneExportFieldCorrespondences,
  KintoneExportMetadataFieldCorrespondences,
  kintoneFieldReadableTypeName,
  KintoneFormField,
  KintoneFormFields,
} from "@graffer-inc/cloud-gov-core";
import {
  FormSchema,
  IFormSchemaV2,
  InputElementUnion,
  isFunctionElement,
  isStaticElement,
  typeLabel,
} from "@graffer-inc/form-schema";

const acceptedInputTypes = Object.entries(
  KintoneExportFieldCorrespondences,
).reduce<Set<string>>((prev, curr) => {
  if (curr[1].length > 0) {
    prev.add(curr[0]);
  }
  return prev;
}, new Set());

export type UseFormSchemaElementCandidatesReturnType = {
  elementsMap: Record<InputElementUnion["id"], InputElementUnion>;
  elements: InputElementUnion[];
};

export const useFormSchemaElementCandidates = (props: {
  formSchema: IFormSchemaV2;
}): UseFormSchemaElementCandidatesReturnType => {
  return React.useMemo(() => {
    const elementsMap: Record<InputElementUnion["id"], InputElementUnion> = {};
    const elements: InputElementUnion[] = [];

    FormSchema.fromJSON(props.formSchema)
      .flattenDeep()
      .forEach((element) => {
        if (isFunctionElement(element)) {
          return;
        }
        if (isStaticElement(element)) {
          return;
        }
        if (!acceptedInputTypes.has(element.inputType)) {
          return;
        }

        // FormSchema.flattenDeep() は、繰り返し項目を、設定されている最大繰り返し回数分だけ展開する仕様なので、
        // 配列の要素がユニークになるよう Map で確認をとった上で、配列に push する。
        if (elementsMap[element.id]) {
          return;
        }
        elementsMap[element.id] = element;
        elements.push(element);
      });

    return {
      elementsMap,
      elements,
    };
  }, [props.formSchema]);
};

export type UseElementSelectReturnType = {
  options: { value: string; label: string }[];
  onInputChange: (text: string) => void;
  isLoading: boolean;
};

export const useElementSelect = ({
  candidates,
}: {
  candidates: Record<InputElementUnion["id"], InputElementUnion>;
}): UseElementSelectReturnType => {
  const [options, setOptions] = React.useState<
    { value: string; label: string }[]
  >([]);
  const [isLoading, setLoading] = React.useState(false);
  const [text, setText] = React.useState("");
  const [debouncedText] = useDebounce(text, 1000);
  React.useEffect(() => {
    if (!debouncedText) {
      setOptions([]);
      setLoading(false);
      return;
    }

    // 部分一致を見たいだけだが、高速化のために正規表現を使う
    let regex: RegExp;
    try {
      regex = new RegExp(debouncedText);
    } catch (error) {
      // 正規表現の構文エラーになる場合は、空配列を返す
      setOptions([]);
      setLoading(false);
      return;
    }
    const result: typeof options = [];
    for (const candidate of Object.values(candidates)) {
      if (regex.test(candidate.label.text) || regex.test(candidate.id)) {
        result.push({
          value: candidate.id,
          label: elementToReadableString(candidate),
        });
      }
      if (result.length >= 20) {
        break;
      }
    }
    setOptions(result);
    setLoading(false);
  }, [debouncedText, setOptions, setLoading, candidates]);

  const onInputChange = React.useCallback(
    (text: string) => {
      setText(text);
      if (text.length > 0) {
        setLoading(true);
      }
    },
    [setText, setLoading],
  );
  return {
    options,
    onInputChange,
    isLoading,
  };
};

export const elementToReadableString = (element: InputElementUnion): string => {
  return `${element.label.text} (${element.id}) (${typeLabel(
    element.inputType,
  )})`;
};

const acceptedKintoneFieldTypes = [
  ...Object.entries(KintoneExportFieldCorrespondences),
  ...Object.entries(KintoneExportMetadataFieldCorrespondences),
].reduce<Set<string>>((prev, curr) => {
  curr[1].forEach((type) => {
    prev.add(type);
  });
  return prev;
}, new Set());

export const useKintoneFieldCandidates = <
  T extends KintoneFormFields | undefined,
>({
  kintoneFormFields,
  fieldTypeFilterOpt,
}: {
  kintoneFormFields: T;
  fieldTypeFilterOpt?: KintoneFormField["type"][];
}): { filteredKintoneFields: T } => {
  return React.useMemo(() => {
    if (!kintoneFormFields) {
      return {
        filteredKintoneFields: kintoneFormFields,
      };
    }
    const filteredKintoneFields: KintoneFormFields = {};
    Object.entries(kintoneFormFields).forEach(([key, value]) => {
      if (isKintoneSubtableFormField(value)) {
        Object.values(value.fields).forEach((subtableField) => {
          if (acceptedKintoneFieldTypes.has(subtableField.type)) {
            if (
              fieldTypeFilterOpt &&
              fieldTypeFilterOpt.every(
                (filterType) => filterType !== subtableField.type,
              )
            ) {
              return;
            }
            filteredKintoneFields[subtableField.code] = subtableField;
          }
        });
        return;
      }

      if (acceptedKintoneFieldTypes.has(value.type)) {
        if (
          fieldTypeFilterOpt &&
          fieldTypeFilterOpt.every((filterType) => filterType !== value.type)
        ) {
          return;
        }
        filteredKintoneFields[key] = value;
      }
    });
    return {
      filteredKintoneFields: filteredKintoneFields as T,
    };
  }, [kintoneFormFields, fieldTypeFilterOpt]);
};

export type UseKintoneFieldSelectReturnType = {
  options: { value: string; label: string }[];
  onInputChange: (text: string) => void;
  isLoading: boolean;
};

export const useKintoneFieldSelect = ({
  candidates,
}: {
  candidates: KintoneFormFields;
}): UseKintoneFieldSelectReturnType => {
  const [options, setOptions] = React.useState<
    { value: string; label: string }[]
  >([]);
  const [isLoading, setLoading] = React.useState(false);
  const [text, setText] = React.useState("");
  const [debouncedText] = useDebounce(text, 1000);
  React.useEffect(() => {
    if (!debouncedText) {
      setOptions([]);
      setLoading(false);
      return;
    }

    // 部分一致を見たいだけだが、高速化のために正規表現を使う
    let regex: RegExp;
    try {
      regex = new RegExp(debouncedText);
    } catch (error) {
      // 正規表現の構文エラーになる場合は、空配列を返す
      setOptions([]);
      setLoading(false);
      return;
    }
    const result: typeof options = [];
    for (const candidate of Object.values(candidates)) {
      if (regex.test(candidate.label) || regex.test(candidate.code)) {
        result.push({
          value: candidate.code,
          label: kintoneFieldToReadableString(candidate),
        });
      }
      if (result.length >= 20) {
        break;
      }
    }
    setOptions(result);
    setLoading(false);
  }, [debouncedText, setOptions, setLoading, candidates]);

  const onInputChange = React.useCallback(
    (text: string) => {
      setText(text);
      if (text.length > 0) {
        setLoading(true);
      }
    },
    [setText, setLoading],
  );
  return {
    options,
    onInputChange,
    isLoading,
  };
};

export const kintoneFieldToReadableString = (
  field: KintoneFormFields[string],
): string => {
  return `${field.label} (${field.code}) (${kintoneFieldReadableTypeName(
    field.type,
  )})`;
};

// arr が変化するたびに arr と同じ数の id を生成し直す
// 直前の描画と同じ状態を避けることを志向する
export const useFreshIds = (arr: Array<unknown>, prefix: string): string[] => {
  const [refreshCount, setRefreshCount] = React.useState<number>(0);
  React.useEffect(() => {
    setRefreshCount((prev) => {
      // 単に、オブジェクトが異なるときに直前の描画と同じ状態を避けることを志向しており、
      // 永続的にユニークな id を生成することを目的としていないので、
      // overflow を避けるためにも適当なところで0へ戻す
      if (prev > 5) {
        return 0;
      }
      return prev + 1;
    });
  }, [arr, setRefreshCount]);
  return useId(arr.length, `${prefix}-${refreshCount}-`);
};
